import { nowEpochMilliseconds } from "@nijiurachan/js/util/time";
import {
  create,
  type Mutate,
  type StoreApi,
  type UseBoundStore,
} from "zustand";
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware";
import { getPostAttachments, type Post } from "@/entities/post";
import { getPostPlainTextWithPoll } from "@/entities/post/pollText";
import type { Thread } from "@/entities/thread";
import { getThreadTitle } from "@/entities/thread";
import { resolveSafePersistStorage } from "@/shared/lib/safePersistStorage";
import { migrateThreadIdArray } from "@/shared/lib/threadIdMigration";

export const IMAGE_NG_SIMILARITY_THRESHOLD = 0.95;
const LEGACY_IMAGE_NG_STORAGE_KEY = "futaba_image_ng";
const LEGACY_TEXT_NG_STORAGE_KEY = "futaba_ng_settings";
export const NG_DISPLAY_ID_EXPIRY_MS: number = 3 * 24 * 60 * 60 * 1000;
const NG_STORE_VERSION = 3;

export interface NgImageEntry {
  hash: string;
  addedAt: number;
  note: string;
}
export interface NgDisplayIdEntry {
  id: string;
  addedAt: number;
}

export interface NgImageAddResult {
  success: boolean;
  message: string;
}

export interface NgState {
  enabled: boolean;
  showNgContent: boolean;
  hiddenThreadIds: string[];
  ngDisplayIds: NgDisplayIdEntry[];
  ngTitles: string[];
  ngWords: string[];
  ngRegexes: string[];
  ngImages: NgImageEntry[];
  legacyImageNgMigrated: boolean;
  legacyTextNgMigrated: boolean;

  setEnabled: (enabled: boolean) => void;
  setShowNgContent: (show: boolean) => void;
  hideThread: (id: string) => void;
  unhideThread: (id: string) => void;
  addNgDisplayId: (displayId: string) => void;
  removeNgDisplayId: (displayId: string) => void;
  addNgTitle: (title: string) => void;
  removeNgTitle: (title: string) => void;
  addNgWord: (word: string) => void;
  removeNgWord: (word: string) => void;
  addNgRegex: (regex: string) => void;
  removeNgRegex: (regex: string) => void;
  addNgImage: (input: string, note?: string) => NgImageAddResult;
  removeNgImage: (bits: string) => void;
  clearNgImages: () => void;

  isThreadHidden: (thread: Thread) => boolean;
  isPostHidden: (post: Post) => boolean;
  clearAllNgSettings: () => void;
}

// ─── 外部から呼び出し可能なヘルパー関数 ───
// NG処理本体 useNgStore
export type NgStore = UseBoundStore<
  Mutate<StoreApi<NgState>, [["zustand/persist", unknown]]>
>;

type NgStoreApi = Mutate<StoreApi<NgState>, [["zustand/persist", unknown]]>;

export function createNgStore(storage?: StateStorage): NgStore {
  let storeApi: NgStoreApi | undefined;
  let legacyMigrationStarted = false;
  const jsonStorage = createJSONStorage(() =>
    resolveSafePersistStorage(storage),
  );

  return create<NgState>()(
    persist(
      (set, get, api) => {
        storeApi = api;
        return {
          enabled: true,
          showNgContent: false,
          hiddenThreadIds: [],
          ngDisplayIds: [],
          ngTitles: [],
          ngWords: [],
          ngRegexes: [],
          ngImages: [],
          legacyImageNgMigrated: false,
          legacyTextNgMigrated: false,

          setEnabled: (enabled: boolean) => set({ enabled }),
          setShowNgContent: (showNgContent: boolean) => set({ showNgContent }),

          hideThread: (id: string) =>
            set((s: NgState) => ({
              hiddenThreadIds: s.hiddenThreadIds.includes(id)
                ? s.hiddenThreadIds
                : [...s.hiddenThreadIds, id],
            })),

          unhideThread: (id: string) =>
            set((s: NgState) => ({
              hiddenThreadIds: s.hiddenThreadIds.filter((i) => i !== id),
            })),

          addNgDisplayId: (displayId: string) =>
            set((s: NgState) => ({
              ngDisplayIds: [
                ...s.ngDisplayIds.filter((entry) => entry.id !== displayId),
                { id: displayId, addedAt: nowEpochMilliseconds() },
              ],
            })),

          removeNgDisplayId: (displayId: string) =>
            set((s: NgState) => ({
              ngDisplayIds: s.ngDisplayIds.filter(
                (entry) => entry.id !== displayId,
              ),
            })),

          addNgTitle: (title: string) =>
            set((s: NgState) => ({
              ngTitles: s.ngTitles.includes(title)
                ? s.ngTitles
                : [...s.ngTitles, title],
            })),

          removeNgTitle: (title: string) =>
            set((s: NgState) => ({
              ngTitles: s.ngTitles.filter((t) => t !== title),
            })),

          addNgWord: (word: string) =>
            set((s: NgState) => ({
              ngWords: s.ngWords.includes(word)
                ? s.ngWords
                : [...s.ngWords, word],
            })),

          removeNgWord: (word: string) =>
            set((s: NgState) => ({
              ngWords: s.ngWords.filter((w) => w !== word),
            })),

          addNgRegex: (regex: string) => {
            try {
              new RegExp(regex);
            } catch {
              return;
            }
            set((s: NgState) => ({
              ngRegexes: s.ngRegexes.includes(regex)
                ? s.ngRegexes
                : [...s.ngRegexes, regex],
            }));
          },

          removeNgRegex: (regex: string) =>
            set((s: NgState) => ({
              ngRegexes: s.ngRegexes.filter((r) => r !== regex),
            })),

          addNgImage: (input: string, note: string = "") => {
            const bits = /^[01]{64}$/.test(input) ? input : decodeNgHash(input);
            if (!bits) {
              return {
                success: false,
                message: "画像ハッシュが取得できませんでした",
              };
            }
            if (
              get().ngImages.some((entry) => isSimilarHash(entry.hash, bits))
            ) {
              return {
                success: false,
                message: "既に類似の画像がNGに登録されています",
              };
            }
            set((s: NgState) => ({
              ngImages: [
                ...s.ngImages,
                {
                  hash: bits,
                  addedAt: nowEpochMilliseconds(),
                  note: note.trim(),
                },
              ],
            }));
            return { success: true, message: "画像をNGに追加しました" };
          },

          removeNgImage: (bits: string) =>
            set((s: NgState) => ({
              ngImages: s.ngImages.filter((entry) => entry.hash !== bits),
            })),

          clearNgImages: () => set({ ngImages: [] }),

          isThreadHidden: (thread: Thread) => {
            const state = get();
            if (!state.enabled) return false;
            if (state.hiddenThreadIds.includes(thread.id)) return true;

            const title = getThreadTitle(thread);
            const body = getPostPlainTextWithPoll(thread.opPost);
            const text = `${title} ${body}`;
            const foldedText = text.toLocaleLowerCase();

            for (const ng of state.ngTitles) {
              // NGスレ文はタイトル抜粋を超えたOP本文全体にも部分一致で当てる
              if (text.includes(ng)) return true;
            }

            for (const ng of state.ngWords) {
              if (foldedText.includes(ng.toLocaleLowerCase())) return true;
            }

            for (const pattern of state.ngRegexes) {
              try {
                if (new RegExp(pattern, "i").test(text)) return true;
              } catch {
                // Invalid regex, skip
              }
            }

            if (
              getPostAttachments(thread.opPost).some((attachment) =>
                isNgAttachment(attachment.ngHash, state.ngImages),
              )
            ) {
              return true;
            }

            return false;
          },

          isPostHidden: (post: Post) => {
            const state = get();
            if (!state.enabled) return false;

            // NG照合は装飾マーカー(PUA)を除いたプレーン本文で行う
            const joinedBody = getPostPlainTextWithPoll(post);

            // displayIdでチェック
            const now = nowEpochMilliseconds();
            if (
              post.displayId &&
              state.ngDisplayIds.some(
                (entry) =>
                  entry.id === post.displayId &&
                  now - entry.addedAt < NG_DISPLAY_ID_EXPIRY_MS,
              )
            ) {
              return true;
            }

            for (const ng of state.ngWords) {
              if (
                joinedBody.toLocaleLowerCase().includes(ng.toLocaleLowerCase())
              )
                return true;
            }

            for (const pattern of state.ngRegexes) {
              try {
                if (new RegExp(pattern, "i").test(joinedBody)) return true;
              } catch {
                // Invalid regex, skip
              }
            }

            if (
              getPostAttachments(post).some((attachment) =>
                isNgAttachment(attachment.ngHash, state.ngImages),
              )
            ) {
              return true;
            }

            return false;
          },

          clearAllNgSettings: () =>
            set({
              enabled: true,
              showNgContent: false,
              hiddenThreadIds: [],
              ngDisplayIds: [],
              ngTitles: [],
              ngWords: [],
              ngRegexes: [],
              ngImages: [],
            }),
        };
      },
      {
        name: "nijiurachan-ng-settings",
        version: NG_STORE_VERSION,
        storage: jsonStorage,
        migrate: migrateNgState,
        onRehydrateStorage:
          () => (state: NgState | undefined, error: unknown) => {
            if (
              error ||
              !state ||
              (state.legacyImageNgMigrated && state.legacyTextNgMigrated) ||
              legacyMigrationStarted
            ) {
              return;
            }
            legacyMigrationStarted = true;

            const persistStorage = storeApi?.persist.getOptions().storage;
            if (!persistStorage) return;

            try {
              const legacyValue = persistStorage.getItem(
                LEGACY_IMAGE_NG_STORAGE_KEY,
              );
              const legacyTextValue = persistStorage.getItem(
                LEGACY_TEXT_NG_STORAGE_KEY,
              );
              if (legacyValue instanceof Promise) {
                void Promise.all([
                  legacyValue,
                  Promise.resolve(legacyTextValue),
                ]).then(
                  ([value, textValue]) =>
                    completeLegacyNgMigration(storeApi, value, textValue),
                  () =>
                    completeLegacyNgMigration(storeApi, undefined, undefined),
                );
                return;
              }
              if (legacyTextValue instanceof Promise) {
                void legacyTextValue.then((value) =>
                  completeLegacyNgMigration(storeApi, legacyValue, value),
                );
                return;
              }
              completeLegacyNgMigration(storeApi, legacyValue, legacyTextValue);
            } catch {
              completeLegacyNgMigration(storeApi, undefined, undefined);
            }
          },
      },
    ),
  );
}

export const useNgStore: NgStore = createNgStore();
// ─── ユーティリティ関数 ───

/** URL-safe Base64 → 64桁ビット文字列 */
export function decodeNgHash(base64: string): string | null {
  if (!/^[A-Za-z0-9_-]{11}$/.test(base64)) return null;
  const std = base64.replace(/-/g, "+").replace(/_/g, "/");
  const padded = std + "=".repeat((4 - (std.length % 4)) % 4);
  let bin: string;
  try {
    bin = atob(padded);
  } catch {
    return null;
  }
  let bits = "";
  for (let i = 0; i < bin.length; i++) {
    bits += bin.charCodeAt(i).toString(2).padStart(8, "0");
  }
  return bits.length === 64 && encodeNgHash(bits) === base64 ? bits : null;
}

export function computeNgHashSimilarity(left: string, right: string): number {
  if (!/^[01]{64}$/.test(left) || !/^[01]{64}$/.test(right)) return 0;
  let same = 0;
  for (let index = 0; index < 64; index += 1) {
    if (left[index] === right[index]) same += 1;
  }
  return same / 64;
}

function isSimilarHash(left: string, right: string): boolean {
  return computeNgHashSimilarity(left, right) >= IMAGE_NG_SIMILARITY_THRESHOLD;
}

function isNgAttachment(
  publicHash: string | null | undefined,
  entries: NgImageEntry[],
): boolean {
  if (!publicHash) return false;
  const bits = decodeNgHash(publicHash);
  return (
    bits !== null && entries.some((entry) => isSimilarHash(entry.hash, bits))
  );
}

export function migrateNgState(persisted: unknown, version: number): unknown {
  const withThreadIds = migrateThreadIdArray(
    persisted,
    version,
    1,
    "hiddenThreadIds",
  );
  if (!isRecord(withThreadIds)) return withThreadIds;
  const ngImages = Array.isArray(withThreadIds.ngImages)
    ? withThreadIds.ngImages.flatMap(normalizePersistedNgImage)
    : [];
  const now = nowEpochMilliseconds();
  const ngDisplayIds = Array.isArray(withThreadIds.ngDisplayIds)
    ? withThreadIds.ngDisplayIds.flatMap((value) =>
        normalizeDisplayId(value, now),
      )
    : [];
  return { ...withThreadIds, ngImages, ngDisplayIds };
}

function completeLegacyNgMigration(
  storeApi: NgStoreApi | undefined,
  legacyValue: unknown,
  legacyTextValue: unknown,
): void {
  if (!storeApi) return;
  const currentState = storeApi.getState();
  const legacyImages = normalizeLegacyNgImages(legacyValue);
  const ngImages = mergeNgImages(currentState.ngImages, legacyImages);
  const legacyText = normalizeLegacyTextNg(legacyTextValue);
  storeApi.setState({
    ngImages,
    ngWords: [...new Set([...currentState.ngWords, ...legacyText.words])],
    ngRegexes: [...new Set([...currentState.ngRegexes, ...legacyText.regexes])],
    ngDisplayIds: mergeDisplayIds(currentState.ngDisplayIds, legacyText.ids),
    legacyImageNgMigrated: true,
    legacyTextNgMigrated: true,
  });
}

function normalizeDisplayId(
  value: unknown,
  fallback: number,
): NgDisplayIdEntry[] {
  if (typeof value === "string") return [{ id: value, addedAt: fallback }];
  if (!isRecord(value) || typeof value.id !== "string") return [];
  return [
    {
      id: value.id,
      addedAt: typeof value.addedAt === "number" ? value.addedAt : fallback,
    },
  ];
}

function normalizeLegacyTextNg(value: unknown): {
  words: string[];
  regexes: string[];
  ids: NgDisplayIdEntry[];
} {
  if (!isRecord(value)) return { words: [], regexes: [], ids: [] };
  const strings = (key: string): string[] =>
    Array.isArray(value[key])
      ? value[key].filter((item): item is string => typeof item === "string")
      : [];
  const now = nowEpochMilliseconds();
  const rawIds = Array.isArray(value.ids)
    ? value.ids
    : Array.isArray(value.ngIds)
      ? value.ngIds
      : [];
  return {
    words: [...strings("words"), ...strings("ngWords")],
    regexes: [...strings("regexes"), ...strings("ngRegexes")],
    ids: rawIds.flatMap((item) => normalizeDisplayId(item, now)),
  };
}

function mergeDisplayIds(
  existing: NgDisplayIdEntry[],
  legacy: NgDisplayIdEntry[],
): NgDisplayIdEntry[] {
  const merged = new Map(existing.map((entry) => [entry.id, entry]));
  for (const entry of legacy)
    if (!merged.has(entry.id)) merged.set(entry.id, entry);
  return [...merged.values()];
}

function normalizeLegacyNgImages(value: unknown): NgImageEntry[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (
      !isRecord(entry) ||
      typeof entry.hash !== "string" ||
      !/^[01]{64}$/.test(entry.hash) ||
      typeof entry.addedAt !== "number" ||
      !Number.isFinite(entry.addedAt) ||
      typeof entry.note !== "string"
    ) {
      return [];
    }
    return [
      {
        hash: entry.hash,
        addedAt: entry.addedAt,
        note: entry.note,
      },
    ];
  });
}

function mergeNgImages(
  existingImages: NgImageEntry[],
  legacyImages: NgImageEntry[],
): NgImageEntry[] {
  const mergedImages = [...existingImages];
  for (const legacyImage of legacyImages) {
    if (
      !mergedImages.some((existingImage) =>
        isSimilarHash(existingImage.hash, legacyImage.hash),
      )
    ) {
      mergedImages.push(legacyImage);
    }
  }
  return mergedImages;
}

function normalizePersistedNgImage(value: unknown): NgImageEntry[] {
  if (typeof value === "string" && /^[01]{64}$/.test(value)) {
    return [{ hash: value, addedAt: 0, note: "" }];
  }
  if (!isRecord(value) || typeof value.hash !== "string") return [];
  const hash = /^[01]{64}$/.test(value.hash)
    ? value.hash
    : decodeNgHash(value.hash);
  if (!hash) return [];
  return [
    {
      hash,
      addedAt:
        typeof value.addedAt === "number" && Number.isFinite(value.addedAt)
          ? value.addedAt
          : 0,
      note: typeof value.note === "string" ? value.note : "",
    },
  ];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** 64桁ビット文字列 → URL-safe Base64（UI表示用） */
export function encodeNgHash(bits: string): string {
  const bytes: number[] = [];
  for (let i = 0; i < 64; i += 8) {
    bytes.push(parseInt(bits.slice(i, i + 8), 2));
  }
  const std = btoa(String.fromCharCode(...bytes));
  return std.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
